import React, { useEffect, useRef, useState } from 'react';
import { cardLogin } from '../services/api';
import { usePersistentFocus } from '../hooks/usePersistentFocus';
import { navigateTo } from '../utils/navigation';
import {
  EMP_CARD_RE,
  normalizeScannedCode,
} from '../utils/pdaCodes';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifySuccess } from '../utils/pdaFeedback';
import { hasStoredCompany, hasStoredSession, storeSession } from '../utils/session';

const AUTO_SCAN_DELAY_MS = 800;

function Login() {
  const inputRef = useRef<HTMLInputElement>(null);
  const scanTimerRef = useRef<number>(0);
  const lastInputAtRef = useRef<number>(0);
  const scannerLikelyRef = useRef<boolean>(false);
  const [scanValue, setScanValue] = useState('');
  const [loading, setLoading] = useState(false);

  usePersistentFocus(inputRef, !loading, loading ? 'loading' : 'ready');
  useEffect(() => () => window.clearTimeout(scanTimerRef.current), []);

  if (hasStoredSession()) {
    navigateTo(hasStoredCompany() ? '/home' : '/company-select', true);
    return null;
  }

  function clearScanInput() {
    window.clearTimeout(scanTimerRef.current);
    lastInputAtRef.current = 0;
    scannerLikelyRef.current = false;
    setScanValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }

  function scheduleLogin(rawCode: string) {
    if (loading) {
      return;
    }

    const now = Date.now();
    const previousValue = scanValue;
    const inputGap = lastInputAtRef.current ? now - lastInputAtRef.current : Number.MAX_SAFE_INTEGER;
    const appendedLength = rawCode.length - previousValue.length;
    const isScannerLikeInput = appendedLength > 1 || inputGap < 60 || (!previousValue && rawCode.length >= 8);

    lastInputAtRef.current = now;
    scannerLikelyRef.current = scannerLikelyRef.current || isScannerLikeInput;
    setScanValue(rawCode);

    window.clearTimeout(scanTimerRef.current);
    scanTimerRef.current = window.setTimeout(() => {
      const latestValue = inputRef.current?.value ?? rawCode;
      const latestNormalized = normalizeScannedCode(latestValue);

      if (!scannerLikelyRef.current || !EMP_CARD_RE.test(latestNormalized)) {
        return;
      }

      void handleLogin(latestValue, true);
    }, AUTO_SCAN_DELAY_MS);
  }

  async function handleLogin(rawCode?: string, silentInvalid = false) {
    const cardCode = normalizeScannedCode(rawCode ?? inputRef.current?.value ?? scanValue);

    if (!cardCode || loading) {
      return;
    }

    if (!EMP_CARD_RE.test(cardCode)) {
      if (!silentInvalid) {
        notifyError('这不是员工卡，请重新扫');
      }
      return;
    }

    setLoading(true);
    try {
      const response = await cardLogin(cardCode);
      const user = response.data;

      storeSession(user);
      notifySuccess(`${user.full_name || user.username || user.emp_no || '员工'}，登录成功`);
      navigateTo('/company-select', true);
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '登录失败'));
      clearScanInput();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">登录</div>
        <div className="auth-title">登录扫码</div>
        <div className="auth-subtitle">
          请扫描员工卡登录
          <br />
          员工卡格式：EMP:SY000101:v1
        </div>

        <div className="auth-body">
          <div className="scan-field">
            <label className="scan-label" htmlFor="login-card-code">
              员工卡
            </label>
            <input
              autoComplete="off"
              autoFocus
              autoCapitalize="off"
              autoCorrect="off"
              className="scan-input"
              id="login-card-code"
              inputMode="text"
              onBlur={() => {
                if (!loading) {
                  window.setTimeout(() => inputRef.current?.focus(), 30);
                }
              }}
              onInput={(event) => scheduleLogin(event.currentTarget.value)}
              onKeyUp={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleLogin(event.currentTarget.value);
                }
              }}
              placeholder="扫描员工卡..."
              ref={inputRef}
              spellCheck={false}
            />
          </div>

          <div className="button-row" style={{ marginTop: 16 }}>
            <button
              className="primary-btn"
              disabled={loading || !scanValue.trim()}
              onClick={() => void handleLogin()}
              type="button"
            >
              {loading ? '登录中...' : '登录'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
